import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiCall } from '../utils/api';
import { reorderStep } from '../utils/stepReorder';
import config from '../config/appConfig';

function ReviewDraftRunbook() {
    const { runbookId } = useParams();
    const navigate = useNavigate();
    
    const [runbook, setRunbook] = useState(null);
    const [editedRunbook, setEditedRunbook] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [hasChanges, setHasChanges] = useState(false);
    const [uploadingImages, setUploadingImages] = useState({});
    const [imageUrls, setImageUrls] = useState({});
    const [highlightedStep, setHighlightedStep] = useState(null);

    useEffect(() => {
        fetchDraftRunbook();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [runbookId]);

    useEffect(() => {
        if (runbook && editedRunbook) {
            checkForChanges();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editedRunbook]);

    const fetchDraftRunbook = async () => {
        try {
            setLoading(true);
            const response = await apiCall(`/runbooks/drafts/${runbookId}`, {
                method: 'GET'
            });

            setRunbook(response.runbook);
            setEditedRunbook(JSON.parse(JSON.stringify(response.runbook))); // Deep copy
            
            // Extract existing image URLs from steps
            const existingImages = {};
            if (response.runbook.Steps) {
                response.runbook.Steps.forEach(step => {
                    if (step.image_url) {
                        existingImages[step.step_number] = step.image_url;
                    }
                });
            }
            setImageUrls(existingImages);
            
            setLoading(false);
        } catch (err) {
            console.error('Failed to fetch draft runbook:', err);
            setError(err.message || 'Failed to load draft runbook');
            setLoading(false);
        }
    };

    const checkForChanges = () => {
        const changed = JSON.stringify(runbook) !== JSON.stringify(editedRunbook);
        setHasChanges(changed);
    };

    const handleInputChange = (field, value) => {
        setEditedRunbook(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleTagsChange = (value) => {
        const tags = value.split(',').map(tag => tag.trim()).filter(tag => tag);
        setEditedRunbook(prev => ({
            ...prev,
            Tags: tags
        }));
    };

    const handleStepChange = (index, field, value) => {
        const newSteps = [...editedRunbook.Steps];
        newSteps[index] = {
            ...newSteps[index],
            [field]: field === 'step_number' ? parseInt(value) || 0 : value
        };
        setEditedRunbook(prev => ({
            ...prev,
            Steps: newSteps
        }));
    };

    const handleAddStep = () => {
        const newStepNumber = editedRunbook.Steps.length + 1;
        const newSteps = [
            ...editedRunbook.Steps,
            {
                step_number: newStepNumber,
                step_description: ''
            }
        ];
        setEditedRunbook(prev => ({
            ...prev,
            Steps: newSteps
        }));
    };

    const handleDeleteStep = (index) => {
        if (!window.confirm('Are you sure you want to delete this step?')) {
            return;
        }

        const deletedStep = editedRunbook.Steps[index];
        console.log('[DELETE STEP] Deleting step:', {
            step_number: deletedStep.step_number,
            has_image: !!deletedStep.image_s3_key,
            s3_key: deletedStep.image_s3_key || 'none'
        });
        
        // Remove the step from array
        const newSteps = editedRunbook.Steps.filter((_, idx) => idx !== index);
        
        // Renumber steps in frontend state only
        const renumberedSteps = newSteps.map((step, idx) => ({
            ...step,
            step_number: idx + 1
        }));
        
        console.log('[DELETE STEP] Steps after deletion and renumbering:', renumberedSteps.length);
        
        // Update image URLs state to match new step numbers
        const updatedImageUrls = {};
        renumberedSteps.forEach((step, idx) => {
            const oldStepNumber = editedRunbook.Steps[idx < index ? idx : idx + 1].step_number;
            if (imageUrls[oldStepNumber]) {
                updatedImageUrls[idx + 1] = imageUrls[oldStepNumber];
            }
        });
        
        setImageUrls(updatedImageUrls);
        
        // Update edited runbook with renumbered steps
        // Image S3 keys remain unchanged (still pointing to temp/permanent location)
        // Backend will handle S3 cleanup and renumbering on save/approve by:
        // 1. Comparing with DB steps to find deleted steps
        // 2. Deleting orphaned images (not renumbered ones)
        // 3. Renumbering remaining images as needed
        setEditedRunbook(prev => ({
            ...prev,
            Steps: renumberedSteps
        }));
        
        console.log('[DELETE STEP] Frontend updated. S3 cleanup will happen on save/approve.');
        console.log('[DELETE STEP] Backend will delete image from deleted step and renumber remaining images.');
    };

    const handleMoveStep = (index, direction) => {
        const currentSteps = editedRunbook.Steps || [];
        const result = reorderStep(currentSteps, imageUrls, index, direction);
        if (!result) return;

        setImageUrls(result.imageUrls);
        setEditedRunbook(prev => ({
            ...prev,
            Steps: result.steps
        }));

        // Highlight the moved step at its new position
        setHighlightedStep(result.newIndex + 1);
        setTimeout(() => setHighlightedStep(null), 1200);

        console.log(`[REORDER] Moved step from position ${index + 1} ${direction} to position ${result.newIndex + 1}`);
    };

    const handleImageUpload = async (stepNumber, file) => {
        if (!file) return;

        // Validate file type
        const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp', 'image/webp'];
        if (!validTypes.includes(file.type)) {
            alert('Please upload a valid image file (JPEG, PNG, GIF, BMP, WEBP)');
            return;
        }

        // Validate file size (max 5MB)
        const maxSize = 5 * 1024 * 1024;
        if (file.size > maxSize) {
            alert('Image size must be less than 5MB');
            return;
        }

        try {
            setUploadingImages(prev => ({ ...prev, [stepNumber]: true }));

            // Convert file to base64
            const reader = new FileReader();
            reader.readAsDataURL(file);
            
            reader.onload = async () => {
                try {
                    const base64Data = reader.result.split(',')[1];

                    const response = await apiCall('/runbooks/upload-step-image', {
                        method: 'POST',
                        body: JSON.stringify({
                            runbookId: editedRunbook.Runbook_ID,
                            tribe: editedRunbook.Tribe,
                            solution: editedRunbook.Solution,
                            stepNumber: stepNumber,
                            image: base64Data,
                            fileName: file.name,
                            isTemp: true  // Temporary upload during draft editing
                        })
                    });

                    if (response.imageUrl) {
                        setImageUrls(prev => ({ ...prev, [stepNumber]: response.imageUrl }));
                        
                        // Update the step with BOTH image_s3_key (for DB) and image_url (for display)
                        const newSteps = editedRunbook.Steps.map(step => {
                            if (step.step_number === stepNumber) {
                                return { 
                                    ...step, 
                                    image_s3_key: response.s3Key,      // Store S3 key for DynamoDB
                                    image_url: response.imageUrl        // Store presigned URL for display
                                };
                            }
                            return step;
                        });
                        
                        setEditedRunbook(prev => ({
                            ...prev,
                            Steps: newSteps
                        }));

                        alert('Image uploaded successfully!');
                    }
                } catch (error) {
                    console.error('Upload error:', error);
                    alert('Failed to upload image: ' + (error.message || 'Unknown error'));
                } finally {
                    setUploadingImages(prev => ({ ...prev, [stepNumber]: false }));
                }
            };

            reader.onerror = () => {
                alert('Failed to read file');
                setUploadingImages(prev => ({ ...prev, [stepNumber]: false }));
            };
        } catch (error) {
            console.error('Image upload error:', error);
            alert('Failed to upload image: ' + (error.message || 'Unknown error'));
            setUploadingImages(prev => ({ ...prev, [stepNumber]: false }));
        }
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            
            console.log('[SAVE] Saving draft changes...');
            console.log('[SAVE] Current steps count:', editedRunbook.Steps?.length || 0);
            console.log('[SAVE] Steps with images:', editedRunbook.Steps?.filter(s => s.image_s3_key).length || 0);
            
            // Backend will:
            // 1. Fetch current DB steps to detect deletions and removed images
            // 2. Delete orphaned images (deleted steps + removed images)
            // 3. Move temp images to permanent location
            // 4. Renumber images as needed (tracked to prevent accidental deletion)
            const response = await apiCall(`/runbooks/drafts/${runbookId}`, {
                method: 'PUT',
                body: JSON.stringify({
                    runbook: editedRunbook
                })
            });

            console.log('[SAVE] Success:', response);

            // Refresh the runbook data to get updated S3 keys and URLs
            await fetchDraftRunbook();
            setHasChanges(false);
            
            alert('Draft runbook saved successfully!');
            setSaving(false);
        } catch (err) {
            console.error('[SAVE] Failed to save draft:', err);
            alert('Failed to save draft: ' + (err.message || 'Unknown error'));
            setSaving(false);
        }
    };

    const handleApprove = async () => {
        if (!window.confirm('Are you sure you want to approve this runbook? It will be moved to the main runbook database.')) {
            return;
        }

        try {
            setSaving(true);
            
            console.log('[APPROVE] Starting approval process...');
            console.log('[APPROVE] Current steps count:', editedRunbook.Steps?.length || 0);
            console.log('[APPROVE] Steps with images:', editedRunbook.Steps?.filter(s => s.image_s3_key).length || 0);
            
            // Log each step being sent to backend
            editedRunbook.Steps?.forEach((step, idx) => {
                console.log(`[APPROVE] Step ${step.step_number}:`, {
                    description: step.step_description?.substring(0, 50),
                    has_image: !!step.image_s3_key,
                    s3_key: step.image_s3_key || 'none'
                });
            });
            
            // Backend will:
            // 1. Fetch current DB steps to detect deletions and removed images
            // 2. Delete orphaned images (deleted steps + removed images)
            // 3. Move temp images to permanent location
            // 4. Renumber images as needed (tracked to prevent accidental deletion)
            // 5. Move to main runbook table
            const response = await apiCall(`/runbooks/drafts/${runbookId}/approve`, {
                method: 'POST',
                body: JSON.stringify({
                    runbook: editedRunbook
                })
            });

            console.log('[APPROVE] Success:', response);
            alert('Runbook approved successfully and moved to main database!');
            navigate('/draft-runbooks');
        } catch (err) {
            console.error('[APPROVE] Failed to approve runbook:', err);
            alert('Failed to approve runbook: ' + (err.message || 'Unknown error'));
            setSaving(false);
        }
    };

    const handleReject = async () => {
        const reason = window.prompt('Please provide a reason for rejection (optional):');
        
        if (reason === null) {
            return; // User cancelled
        }

        try {
            setSaving(true);
            await apiCall(`/runbooks/drafts/${runbookId}`, {
                method: 'DELETE',
                body: JSON.stringify({ reason })
            });

            alert('Draft runbook rejected and deleted');
            navigate('/draft-runbooks');
        } catch (err) {
            console.error('Failed to reject runbook:', err);
            alert('Failed to reject runbook: ' + (err.message || 'Unknown error'));
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="container-fluid mt-4">
                <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2">Loading draft runbook...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container-fluid mt-4">
                <div className="alert alert-danger" role="alert">
                    <h4 className="alert-heading">Error</h4>
                    <p>{error}</p>
                    <button className="btn btn-primary" onClick={() => navigate('/draft-runbooks')}>
                        Back to Draft List
                    </button>
                </div>
            </div>
        );
    }

    if (!editedRunbook) {
        return <div className="container-fluid mt-4"><p>Runbook not found</p></div>;
    }

    return (
        <div className="container-fluid mt-4">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2>Review Draft Runbook</h2>
                <button 
                    className="btn btn-secondary"
                    onClick={() => {
                        if (hasChanges && !window.confirm('You have unsaved changes. Any uploaded images will remain in temporary storage. Continue?')) {
                            return;
                        }
                        navigate('/draft-runbooks');
                    }}
                    disabled={saving}
                >
                    Back to Draft List
                </button>
            </div>

            {/* Change Indicator */}
            {hasChanges && (
                <div className="alert alert-warning">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    You have unsaved changes. These changes will be applied when you approve this runbook.
                </div>
            )}

            {/* Basic Information */}
            <div className="card mb-4">
                <div className="card-header bg-primary text-white">
                    <h5 className="mb-0">Basic Information</h5>
                </div>
                <div className="card-body">
                    <div className="row mb-3">
                        <div className="col-md-6">
                            <label className="form-label"><strong>Runbook ID</strong></label>
                            <input
                                type="text"
                                className="form-control"
                                value={editedRunbook.Runbook_ID}
                                disabled
                            />
                        </div>
                        <div className="col-md-6">
                            <label className="form-label"><strong>Modified By</strong></label>
                            <input
                                type="text"
                                className="form-control"
                                value={editedRunbook['Modified By'] || 'Unknown'}
                                disabled
                            />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-6">
                            <label className="form-label"><strong>Tribe</strong></label>
                            <select
                                className="form-control"
                                value={editedRunbook.Tribe}
                                onChange={(e) => {
                                    handleInputChange('Tribe', e.target.value);
                                    // Reset Solution when Tribe changes
                                    handleInputChange('Solution', '');
                                }}
                            >
                                <option value="">Select Tribe</option>
                                {config.tribes.map((tribe) => (
                                    <option key={tribe} value={tribe}>
                                        {tribe}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-6">
                            <label className="form-label"><strong>Solution</strong></label>
                            <select
                                className="form-control"
                                value={editedRunbook.Solution}
                                onChange={(e) => handleInputChange('Solution', e.target.value)}
                                disabled={!editedRunbook.Tribe}
                            >
                                <option value="">Select Solution</option>
                                {editedRunbook.Tribe && config.solutions[editedRunbook.Tribe] &&
                                    Object.keys(config.solutions[editedRunbook.Tribe]).map((solutionName) => (
                                        <option key={solutionName} value={solutionName}>
                                            {solutionName}
                                        </option>
                                    ))}
                            </select>
                        </div>
                    </div>

                    <div className="mb-3">
                        <label className="form-label"><strong>Title</strong></label>
                        <input
                            type="text"
                            className="form-control"
                            value={editedRunbook.Title}
                            onChange={(e) => handleInputChange('Title', e.target.value)}
                            maxLength={256}
                        />
                        <small className="text-muted">
                            {editedRunbook.Title?.length || 0}/256 characters
                        </small>
                    </div>

                    <div className="mb-3">
                        <label className="form-label"><strong>Tags</strong></label>
                        <input
                            type="text"
                            className="form-control"
                            value={editedRunbook.Tags?.join(', ') || ''}
                            onChange={(e) => handleTagsChange(e.target.value)}
                            placeholder="Enter tags separated by commas"
                        />
                        <small className="text-muted">Separate tags with commas</small>
                    </div>
                </div>
            </div>

            {/* Steps */}
            <div className="card mb-4">
                <div className="card-header bg-success text-white d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Resolution Steps ({editedRunbook.Steps?.length || 0})</h5>
                    <button
                        className="btn btn-light btn-sm"
                        onClick={handleAddStep}
                    >
                        <i className="bi bi-plus-circle me-1"></i>
                        Add Step
                    </button>
                </div>
                <div className="card-body">
                    {editedRunbook.Steps && editedRunbook.Steps.length > 0 ? (
                        editedRunbook.Steps.map((step, index) => (
                            <div
                                key={index}
                                className={`card mb-3 step-card-reorderable ${highlightedStep === step.step_number ? 'step-card-highlight' : ''}`}
                            >
                                <div className="card-body">
                                    <div className="row mb-2">
                                        <div className="col-md-2">
                                            <label className="form-label"><strong>Step #</strong></label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                value={step.step_number}
                                                disabled
                                                min="1"
                                                style={{ textAlign: 'center', fontWeight: '600' }}
                                            />
                                        </div>
                                        <div className="col-md-10">
                                            <label className="form-label">
                                                <strong>Description</strong>
                                            </label>
                                            <textarea
                                                className="form-control"
                                                rows="3"
                                                value={step.step_description}
                                                onChange={(e) => handleStepChange(index, 'step_description', e.target.value)}
                                                placeholder="Enter step description"
                                                style={{ textAlign: 'left', resize: 'vertical', fontSize: '0.875rem', borderRadius: '6px' }}
                                            />
                                        </div>
                                    </div>

                                    {/* Image Upload Section */}
                                    <div className="row mb-3">
                                        <div className="col-md-12">
                                            <label className="form-label">
                                                <strong>Step Image (Optional)</strong>
                                            </label>
                                            <div className="input-group">
                                                <input
                                                    type="file"
                                                    className="form-control"
                                                    accept="image/*"
                                                    onChange={(e) => {
                                                        const file = e.target.files[0];
                                                        if (file) {
                                                            handleImageUpload(step.step_number, file);
                                                        }
                                                    }}
                                                    disabled={uploadingImages[step.step_number]}
                                                />
                                                {uploadingImages[step.step_number] && (
                                                    <span className="input-group-text">
                                                        <span className="spinner-border spinner-border-sm"></span>
                                                    </span>
                                                )}
                                            </div>
                                            <small className="text-muted d-block mt-1">
                                                Upload path: Runbooks/{editedRunbook.Tribe}/{editedRunbook.Solution}/{editedRunbook.Runbook_ID}/step_{step.step_number}/
                                            </small>
                                            {uploadingImages[step.step_number] && (
                                                <div className="alert alert-info mt-2 mb-0">
                                                    <i className="bi bi-cloud-upload me-2"></i>
                                                    Uploading image...
                                                </div>
                                            )}
                                            {imageUrls[step.step_number] && (
                                                <div className="alert alert-success mt-2 mb-0">
                                                    <i className="bi bi-check-circle me-2"></i>
                                                    Image uploaded successfully!
                                                    <div className="mt-3 mb-3">
                                                        <img 
                                                            src={imageUrls[step.step_number]} 
                                                            alt={`Step ${step.step_number}`}
                                                            className="img-fluid rounded border"
                                                            style={{ maxHeight: '300px', maxWidth: '100%', objectFit: 'contain' }}
                                                        />
                                                    </div>
                                                    <a 
                                                        href={imageUrls[step.step_number]} 
                                                        target="_blank" 
                                                        rel="noopener noreferrer"
                                                        className="btn btn-sm btn-outline-primary"
                                                    >
                                                        <i className="bi bi-box-arrow-up-right me-1"></i>
                                                        View Full Size
                                                    </a>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="step-actions-row">
                                        <div className="step-reorder-btn-group">
                                            <button
                                                type="button"
                                                className="step-reorder-btn"
                                                title="Move step up"
                                                onClick={() => handleMoveStep(index, 'up')}
                                                disabled={index === 0}
                                            >
                                                ▲
                                            </button>
                                            <button
                                                type="button"
                                                className="step-reorder-btn"
                                                title="Move step down"
                                                onClick={() => handleMoveStep(index, 'down')}
                                                disabled={index === editedRunbook.Steps.length - 1}
                                            >
                                                ▼
                                            </button>
                                        </div>
                                        <button
                                            className="btn btn-danger btn-sm"
                                            onClick={() => handleDeleteStep(index)}
                                        >
                                            <i className="bi bi-trash me-1"></i>
                                            Delete Step
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="alert alert-warning">
                            No steps defined. Click "Add Step" to create one.
                        </div>
                    )}
                </div>
            </div>

            {/* Action Buttons */}
            <div className="card mb-4">
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-4">
                            <button
                                className="btn btn-success btn-lg w-100"
                                onClick={handleApprove}
                                disabled={saving || !editedRunbook.Steps || editedRunbook.Steps.length === 0}
                            >
                                {saving ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                        Processing...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-check-circle me-2"></i>
                                        Approve Runbook
                                    </>
                                )}
                            </button>
                            {/* <small className="text-muted d-block mt-2">
                                Move to main database{hasChanges && ' with your changes'}
                            </small> */}
                        </div>
                        <div className="col-md-4">
                            <button
                                className="btn btn-primary btn-lg w-100"
                                onClick={handleSave}
                                disabled={saving || !hasChanges}
                            >
                                {saving ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-save me-2"></i>
                                        Save Changes
                                    </>
                                )}
                            </button>
                            {/* <small className="text-muted d-block mt-2">
                                {hasChanges ? 'Save changes to draft table' : 'No changes to save'}
                            </small> */}
                        </div>
                        <div className="col-md-4">
                            <button
                                className="btn btn-danger btn-lg w-100"
                                onClick={handleReject}
                                disabled={saving}
                            >
                                <i className="bi bi-x-circle me-2"></i>
                                Reject & Delete
                            </button>
                            {/* <small className="text-muted d-block mt-2">
                                Delete draft and S3 images
                            </small> */}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ReviewDraftRunbook;
